import { BdGraph, BdGraphStateMap } from '../../../lib/graph/graph.model';
import { EnginePositionModel } from '../models/engine-position.model';
import { ViewTreeNode } from '../models/view.tree-node';
import { HitModel } from '../models/hit.model';
import { ChapterInitModel } from '../models/engine-chapter-init-model';
import { ToolEnum } from '../models/tool.enum';
import { ScrollModeEnum } from '../models/scroll-mode.enum';
import { ColorRecipeModel } from 'src/app/edit/models/color-recipe.model';
import { TransitionModel } from 'src/app/story/models/transition.model';
import { PostProcessingEffect } from '../models/post-processing/effects';
import { TransparencyModeEnum } from '../models/post-processing/transparency-mode.enum';
import { ObjectId, NodeState } from './nodes/engine.nodes.state.model';
import { EngineColorRecipeModel } from 'src/app/edit/models/engine-color-recipe.model';
import { ChapterModel } from 'src/app/story/models/chapter.model';

export class Load {
  static readonly type = '[Engine] Load';
  constructor() { }
}

export class LoadSuccess {
  static readonly type = '[Engine] Load Success';
  constructor(public result: string) { }
}

export class SetReady {
  static readonly type = '[Engine] Set Ready';
  constructor(public ready: boolean) { }
}

export class SetDisplay {
  static readonly type = '[Engine] Set Display of 3D Scene';
  constructor(public display: boolean) { }
}

export class SetScrollMode {
  static readonly type = '[Engine] Set Zoom via scroll Allowed';
  constructor(public scrollMode: ScrollModeEnum) { }
}

export class ShowScrollAlert {
  static readonly type = '[Engine] Set Scroll Alert Shwon';
  constructor(public shown: boolean) { }
}

export class SetVisible {
  static readonly type = '[Engine] Set Visibility of 3D Scene';
  constructor(public visible: boolean) { }
}

export class Fade {
  static readonly type = '[Engine] Fade';
  constructor(public color: string, public duration: number) { }
}

export class FadeComplete {
  static readonly type = '[Engine] Fade Complete';
  constructor() { }
}

export class FadeOut {
  static readonly type = '[Engine] Fade Out';
  constructor() { }
}


/**
 * Scene Handling
 */
export class SetGraph {
  static readonly type = '[Engine] Set Graph';
  constructor(public graph: BdGraph<ViewTreeNode>) { }
}

export class ClearGraph {
  static readonly type = '[Engine] Clear Graph';
  constructor() { }
}

export class SetXrayMode {
  static readonly type = '[Engine] Set Xray Mode';
  constructor(public enabled: boolean) { }
}

export class SetEngineChapter {
  static readonly type = '[Engine] Set Engine Chapter';
  constructor(public chapterId: string) { }
}

export class SetEngineChapterInit {
  static readonly type = '[Eninge] Set Chapter Init';
  constructor(public chapterInit: ChapterInitModel) {}
}

export class ClickNode {
  static readonly type = '[Engine] Click Node';
  constructor(public node: ViewTreeNode, public event: HammerInput, public hit?: HitModel) {}
}

export class RightClickNode {
  static readonly type = '[Engine] Right Click Node';
  constructor(public node: ViewTreeNode, public event: Event, public hit?: HitModel) {}
}

export class SetContextMenu {
  static readonly type = '[Engine] Set Contextmenu';
  constructor(public node: ViewTreeNode, public hit?: HitModel) {}
}

export class FlyToObject {
  static readonly type = '[Engine] Fly To Object';
  constructor(public objectId: string, public animate: boolean = true, public animationDuration?: number) {}
}

export class CenterCameraOnVisible {
  static readonly type = '[Engine] Center Camera on Visible Nodes';
  constructor() {}
}

export class SynchronizeVisibleNodes {
  static readonly type = '[Engine] Synchronize Visible Nodes On Tree';
  constructor(public leafVisibilityState: BdGraphStateMap, public nodes: Record<ObjectId, NodeState>) {}
}

export class SynchronizeRenderableNodes {
  static readonly type = '[Engine] Synchronize Renderable State of Nodes On Tree';
  constructor(public leafRenderableState: BdGraphStateMap) {}
}

export class SynchronizePickableNodes {
  static readonly type = '[Engine] Synchronize Pickable State of Nodes On Tree';
  constructor(public leafPickableState: BdGraphStateMap, public nodes: Record<ObjectId, NodeState>) {}
}

export class Hit {
  static readonly type = '[Engine] Hit';
  constructor(public hit: HitModel) { }
}

export class ToggleCameraBar {
  static readonly type = '[Engine] Toggle Camera Bar';
  constructor(public show: boolean) { }
}

export class ToggleProgressBar {
  static readonly type = '[Engine] Toggle Progress Bar';
  constructor(public show: boolean) { }
}

export class ToggleLabels {
  static readonly type = '[Engine] Toggle Labels';
  constructor(public show?: boolean) { }
}

export class SetActiveTool {
  static readonly type = '[Engine] Set active tool';
  constructor(public tool: ToolEnum) { }
}

export class PlayChapterAnimation {
  static readonly type = '[Engine] Play chapter animation';
  constructor(public chapter: ChapterModel, public pause?: boolean) { }
}
export class ToggleAnimation {
  static readonly type = '[Engine] Play animation';
  constructor(public play = true) { }
}

export class SetAnimationPlaying {
  static readonly type = '[Engine] Set animation playing';
  constructor(public playing: boolean) { }
}

export class SetPlaybackSpeed {
  static readonly type = '[Engine] Set playback speed';
  constructor(public speed: number) { }
}

export class PlayAudio {
  static readonly type = '[Engine] Play audio';
  constructor(public play: boolean) { }
}

export class PlayChapterFromStart {
  static readonly type = '[Engine] Play chapter from start';
  constructor() { }
}

export class SetAutoplay {
  static readonly type = '[Engine] Set autoplay';
  constructor(public enable: boolean) { }
}

export class SetObjectLayerOrder {
  static readonly type = '[Engine] Set object layer order';
  constructor(public nodes: ViewTreeNode[], public layer: number) {}
}

export class SetObjectDisplayName {
  static readonly type = '[Engine] Set object display name';
  constructor(public nodeId: string, public displayName: string) {}
}

export class AutoSortObjectLayers {
  static readonly type = '[Engine] Auto Sort object layer orders';
  constructor() {}
}

export class ResetObjectLayers {
  static readonly type = '[Engine] Reset object layer orders';
  constructor() {}
}



/**
 * Canvas element references
 */
export class SetEnginePosition {
  static readonly type = '[Engine] Set Engine Position';
  constructor(public position: EnginePositionModel) { }
}

export class ResetEnginePosition {
  static readonly type = '[Engine] Reset Engine Position';
  constructor() { }
}

export class ResetEngineInteractions {
  static readonly type = '[Engine] Reset Interactions';
  constructor() { }
}

// Post Processing
export class SetPostProcessingEffects {
  static readonly type = '[Engine] Set Post Processing Effects';
  constructor(public effects: PostProcessingEffect[]) { }
}

export class SetProperties {
  static readonly type = '[Engine] Set Properties';
  constructor(public properties: { [key: string]: any; }) { }
}

export class SetTransparencyMode {
  static readonly type = '[Engine] Set Transparency Mode';
  constructor(public mode: TransparencyModeEnum) { }
}

export class ExpandViewTreeNode {
  static readonly type = '[Engine] Expand ViewTree Node';
  constructor(public node: ViewTreeNode) {}
}

export class CollapseViewTreeNode {
  static readonly type = '[Engine] Collapse ViewTree Node';
  constructor(public node: ViewTreeNode) {}
}

export class HoverNode {
  static readonly type = '[Engine] Hover Node';
  constructor(public node: ViewTreeNode, public hit?: HitModel) {}
}

export class SetLabelEditNode {
  static readonly type = '[Engine] Set Label Edit Node';
  constructor(public node: ViewTreeNode) {}
}

export class SetNodeInfo {
  static readonly type = '[Engine] Set Node Info';
  constructor(public node: ViewTreeNode, public description: string) {}
}

export class QueryNodeInfo {
  static readonly type = '[Engine] Query Node Info';
  constructor(public node: ViewTreeNode) {}
}

export class QueryNodeInfoSuccess {
  static readonly type = '[Engine] Query Node Info Success';
  constructor(public node: ViewTreeNode, public description: string) {}
}

export class QueryNodeInfoFailure {
  static readonly type = '[Engine Nodes] Query Node Info Failure';
  constructor(public node: ViewTreeNode) {}
}
